import {MAIN_ROOM_ID} from '../constants';
import {fetchWithInfoLog} from '../fetcher';
import {logger} from '../logger';
import type {
    APIType,
    Call,
    ClientProps,
    GenerateEndpoint,
    GetEndpointParams,
    GetEndpointResponse,
    InfinityClient,
    InfinitySignals,
    Participant,
    ParticipantID,
    RoomID,
} from '../types';

/**
 * Creates participant-level action handlers.
 *
 * These actions map to the Pexip REST API participant functions
 * (`/api/client/v2/conferences/<alias>/participants/<uuid>/<request>`).
 * Host-only actions are noted in individual docs. Unless specified,
 * most actions default the target to the current participant.
 *
 * All actions support breakout rooms via automatic routing.
 *
 * @see {@link https://docs.pexip.com/api_client/api_rest.htm | Pexip client REST API}
 */
export const createParticipantsActions = ({
    API,
    signals,
    clientProps,
    generateEndpoint,
    getMe,
    getCall,
    getCallUuid,
    getParticipants,
    getBreakoutParticipantUuid,
}: {
    API: APIType;
    clientProps: ClientProps;
    generateEndpoint: GenerateEndpoint;
    getMe: (roomId?: RoomID) => Participant | undefined;
    getCall: () => Call | undefined;
    getCallUuid: () => string | undefined;
    getParticipants: () => {
        get: (roomID: RoomID, uuid: ParticipantID) => Participant | undefined;
    };
    getBreakoutParticipantUuid: (
        breakoutUuid: string | undefined,
    ) => string | undefined;
    signals: InfinitySignals;
}) => {
    type Defaults = Pick<
        ClientProps,
        'conferenceAlias' | 'host' | 'breakoutUuid'
    >;
    const withDefaults = <T>(p: T): Defaults & T =>
        ({
            conferenceAlias: clientProps.conferenceAlias,
            host: clientProps.host,
            breakoutUuid: clientProps.breakoutUuid,
            ...p,
        }) as Defaults & T;

    /**
     * Admits a participant from the waiting room of a locked conference.
     *
     * Host-only.
     *
     * REST: `POST /participants/<uuid>/unlock`
     */
    const admit = generateEndpoint({
        func: async ([params]: GetEndpointParams<'admit'>, fetcher) => {
            const {participantUuid, conferenceAlias, host, breakoutUuid} =
                withDefaults(params);

            return API.unlockParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'admit',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Reports local client-side mute state without server-side muting.
     *
     * Sets the `is_client_muted` property in the participant list.
     * Defaults to self.
     *
     * REST: `POST /participants/<uuid>/client_mute` or `client_unmute`
     */
    const clientMute = generateEndpoint({
        func: async ([params]: GetEndpointParams<'mute'>, fetcher) => {
            const {
                mute,
                participantUuid = getMe()?.uuid ?? clientProps.participantUuid,
                conferenceAlias,
                breakoutUuid,
                host,
            } = withDefaults(params);

            const apiFn = mute
                ? API.clientMuteParticipant
                : API.clientUnmuteParticipant;

            return apiFn({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'mute',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Sends Far End Camera Control (FECC) messages to a participant.
     *
     * Routing logic:
     * - If the target is the current participant, the event is emitted locally.
     * - In direct media mode, FECC is sent via the WebRTC data channel.
     * - In gateway calls (no target), FECC is sent on the call-level endpoint.
     * - Otherwise, the participant-level FECC endpoint is used.
     *
     * REST: `POST /participants/<uuid>/fecc` or
     *       `POST /participants/<uuid>/calls/<callUuid>/fecc`
     */
    const fecc = generateEndpoint({
        func: async ([params]: GetEndpointParams<'fecc'>, fetcher) => {
            const {target, action, movement, timeout} = params;
            const currentCall = getCall();
            const currentCallUuid = getCallUuid();

            if (target === clientProps.participantUuid) {
                signals.onFecc.emit({action, movement, timeout});
                return Promise.resolve({
                    status: 200,
                    data: {status: 'success', result: true},
                } as GetEndpointResponse<'fecc'>);
            }

            if (currentCall && clientProps?.isDirectMedia) {
                currentCall.sendDataChannelEvent({
                    type: 'fecc',
                    body: {
                        action,
                        movement,
                        timeout,
                    },
                });
                return Promise.resolve({
                    status: 200,
                    data: {status: 'success', result: true},
                } as GetEndpointResponse<'fecc'>);
            }

            if (!target) {
                if (!currentCallUuid) {
                    throw new Error('Call uuid is not present');
                }
                return API.fecc({
                    fetcher,
                    params: {
                        conferenceAlias: clientProps.conferenceAlias,
                        participantUuid: clientProps.participantUuid,
                        callUuid: currentCallUuid,
                    },
                    body: {
                        action,
                        movement,
                        timeout,
                    },
                    host: clientProps.host,
                });
            }

            const feccBody = {
                action,
                target,
                movement,
                timeout,
            };

            return API.feccParticipant({
                fetcher,
                params: {
                    conferenceAlias: clientProps.conferenceAlias,
                    breakoutUuid: clientProps.breakoutUuid,
                    participantUuid: target,
                },
                body: feccBody,
                host: clientProps.host,
            });
        },
        funcName: 'fecc',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Disconnects a participant from the conference.
     *
     * Host-only.
     *
     * REST: `POST /participants/<uuid>/disconnect`
     */
    const kick = generateEndpoint({
        func: async ([params]: GetEndpointParams<'kick'>, fetcher) => {
            const {participantUuid, conferenceAlias, host, breakoutUuid} =
                withDefaults(params);

            return API.disconnectParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'kick',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Enables or disables live captions for a participant.
     *
     * A Host can toggle captions for any participant; a Guest can only
     * toggle for themselves. When enabled, `live_captions` events are
     * streamed via the EventSource.
     *
     * REST: `POST /participants/<uuid>/show_live_captions` or `hide_live_captions`
     */
    const liveCaptions = generateEndpoint({
        func: async ([params]: GetEndpointParams<'liveCaptions'>, fetcher) => {
            const {
                enable,
                conferenceAlias,
                participantUuid = clientProps.participantUuid,
                breakoutUuid,
                captionsLanguage,
            } = withDefaults(params);

            const apiFn = enable ? API.showLiveCaptions : API.hideLiveCaptions;

            const resolvedParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid) ?? participantUuid;

            return apiFn({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid: resolvedParticipantUuid,
                    breakoutUuid,
                },
                host: clientProps.host,
                body: {
                    ...(captionsLanguage && {
                        captions_language: captionsLanguage,
                    }),
                },
            });
        },
        funcName: 'liveCaptions',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Server-side mutes or unmutes a participant's audio.
     *
     * Host-only for muting other participants. Defaults to self.
     *
     * REST: `POST /participants/<uuid>/mute` or `unmute`
     */
    const mute = generateEndpoint({
        func: async ([params]: GetEndpointParams<'mute'>, fetcher) => {
            const {
                mute,
                participantUuid = getMe()?.uuid ?? clientProps.participantUuid,
                conferenceAlias,
                breakoutUuid,
                host,
            } = withDefaults(params);

            const apiFn = mute ? API.muteParticipant : API.unmuteParticipant;

            return apiFn({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'mute',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Mutes or unmutes a participant's video.
     *
     * Host-only for muting other participants. Defaults to self.
     *
     * REST: `POST /participants/<uuid>/video_muted` or `video_unmuted`
     */
    const muteVideo = generateEndpoint({
        func: async ([params]: GetEndpointParams<'muteVideo'>, fetcher) => {
            const {
                muteVideo,
                participantUuid = getMe()?.uuid ?? clientProps.participantUuid,
                conferenceAlias,
                breakoutUuid,
                host,
            } = withDefaults(params);

            const apiFn = muteVideo
                ? API.videoMuteParticipant
                : API.videoUnmuteParticipant;

            return apiFn({
                fetcher,
                params: {
                    breakoutUuid,
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'muteVideo',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Notifies the server that the participant is not away from keyboard.
     *
     * Used to dismiss the silent video detection prompt in Adaptive
     * Composition layouts.
     */
    const notifyNotAFK = generateEndpoint({
        func: async ([params]: GetEndpointParams<'notifyNotAFK'>, fetcher) => {
            const {
                conferenceAlias,
                participantUuid = clientProps.participantUuid,
                host,
            } = withDefaults(params);

            const res = await API.notifyNotAFK({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });

            return res;
        },
        funcName: 'notifyNotAFK',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Controls whether the participant sees presentation in the layout mix.
     *
     * Only applies to Adaptive Composition layouts. The target must be
     * the participant making the request.
     *
     * REST: `POST /participants/<uuid>/pres_in_mix`
     */
    const presInMix = generateEndpoint({
        func: async ([params]: GetEndpointParams<'presInMix'>, fetcher) => {
            const {
                state,
                conferenceAlias,
                participantUuid = clientProps.participantUuid,
                host,
            } = withDefaults(params);

            const res = await API.presInMixParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                body: {
                    state,
                },
                host,
            });

            return res;
        },
        funcName: 'presInMix',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Raises or lowers the participant's hand.
     *
     * REST: `POST /participants/<uuid>/buzz` or `clearbuzz`
     */
    const raiseHand = generateEndpoint({
        func: async ([params]: GetEndpointParams<'raiseHand'>, fetcher) => {
            const {
                raise,
                conferenceAlias,
                host,
                participantUuid = getMe()?.uuid ?? clientProps.participantUuid,
                breakoutUuid,
            } = withDefaults(params);

            const apiFn = raise
                ? API.buzzParticipant
                : API.clearbuzzParticipant;

            return apiFn({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'raiseHand',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Sets the aspect ratio the participant would like to receive.
     *
     * The value should be between 0 and 2 (e.g. `0.5625` for 16:9 portrait).
     *
     * REST: `POST /participants/<uuid>/preferred_aspect_ratio`
     */
    const requestAspectRatio = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'requestAspectRatio'>,
            fetcher,
        ) => {
            const {
                aspectRatio,
                conferenceAlias,
                host,
                participantUuid = clientProps.participantUuid,
                abortSignal,
            } = withDefaults(params);

            if (!participantUuid) {
                // FIXME: Before the refactor we didn't cleanup the `currentParticipantUuid` properly
                // which made this pass in between of calls during escalation, but now this is not available sometimes during the transfer
                logger.warn('Participant uuid is not present');
                return undefined;
            }

            try {
                return await API.preferredAspectRatio({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid,
                    },
                    body: {
                        aspect_ratio: aspectRatio,
                    },
                    host,
                    init: {signal: abortSignal},
                });
            } catch (error) {
                // Swollow abort errors as they are expected when the request is cancelled, and rethrow others
                if (
                    error instanceof DOMException &&
                    error.name === 'AbortError'
                ) {
                    return undefined;
                }
                throw error;
            }
        },
        funcName: 'requestAspectRatio',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Sends DTMF digits to a participant or to the current call.
     *
     * When `participantUuid` is omitted, digits are sent on the current
     * call-level DTMF endpoint. When `callUuid` is provided, it targets
     * that specific call. Otherwise, the participant-level DTMF endpoint
     * is used.
     *
     * REST: `POST /participants/<uuid>/dtmf` or
     *       `POST /participants/<uuid>/calls/<callUuid>/dtmf`
     */
    const sendDTMF = generateEndpoint({
        func: async ([params]: GetEndpointParams<'sendDTMF'>, fetcher) => {
            const {
                digits,
                participantUuid,
                callUuid,
                conferenceAlias,
                breakoutUuid,
                host,
            } = withDefaults(params);

            const common = {
                fetcher,
                body: {
                    digits,
                },
                host,
            };

            const currentCallUuid = getCallUuid();

            if (!participantUuid) {
                if (!currentCallUuid) {
                    throw new Error('Call uuid is not present');
                }
                return API.dtmf({
                    ...common,
                    params: {
                        conferenceAlias,
                        participantUuid: clientProps.participantUuid,
                        callUuid: currentCallUuid,
                    },
                });
            }

            if (callUuid) {
                return API.dtmf({
                    ...common,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        callUuid,
                    },
                });
            }

            return API.dtmfParticipant({
                ...common,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                    participantUuid,
                },
            });
        },
        funcName: 'sendDTMF',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Sends a chat message to the conference or to a specific participant.
     *
     * In direct media mode, messages are sent via the WebRTC data channel
     * instead of the REST API. When `participantUuid` is provided, the
     * message is sent directly to that participant; otherwise it is
     * broadcast to the entire conference.
     *
     * REST: `POST /conferences/<alias>/message` or
     *       `POST /participants/<uuid>/message`
     */
    const sendMessageRequest = generateEndpoint({
        func: async ([params]: GetEndpointParams<'sendMessage'>, fetcher) => {
            const {
                payload,
                type,
                participantUuid,
                conferenceAlias,
                host,
                breakoutUuid,
            } = withDefaults(params);

            const currentCall = getCall();

            if (currentCall && clientProps?.isDirectMedia) {
                // Use Logical OR: Fallback display name if the participant don't have a display name explicitly set
                const origin =
                    getParticipants().get(
                        MAIN_ROOM_ID,
                        clientProps.participantUuid,
                    )?.displayName || 'User'; // empty string should be replaced
                currentCall.sendDataChannelEvent({
                    type: 'message',
                    body: {
                        type,
                        origin,
                        uuid: clientProps.participantUuid,
                        payload,
                    },
                });
                return Promise.resolve({
                    status: 200,
                    data: {status: 'success', result: true},
                } as GetEndpointResponse<'sendMessage'>);
            }

            const common = {
                fetcher,
                host,
                body: {
                    payload,
                    type,
                },
            };
            if (participantUuid) {
                return API.messageParticipant({
                    ...common,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        participantUuid,
                    },
                });
            }
            return API.messageConference({
                ...common,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                },
            });
        },
        funcName: 'sendMessage',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
        retriable: true,
    });

    /** Sends a JSON application message. Wraps {@link sendMessageRequest} with `type: "application/json"`. */
    const sendApplicationMessage = async (
        params: Parameters<InfinityClient['sendApplicationMessage']>[0],
    ) => {
        try {
            const payload = JSON.stringify(params.payload);
            return sendMessageRequest({
                ...params,
                payload,
                type: 'application/json',
            });
        } catch (error) {
            logger.error({error}, 'Could not stringify application message');
        }
    };

    /** Sends a plain-text chat message. Wraps {@link sendMessageRequest} with `type: "text/plain"`. */
    const sendMessage = async (
        params: Parameters<InfinityClient['sendMessage']>[0],
    ) => {
        return sendMessageRequest({...params, type: 'text/plain'});
    };

    /**
     * Changes a participant's role between Host (`"chair"`) and Guest (`"guest"`).
     *
     * Host-only.
     *
     * REST: `POST /participants/<uuid>/role`
     */
    const setRole = generateEndpoint({
        func: async ([params]: GetEndpointParams<'setRole'>, fetcher) => {
            const {role, participantUuid, conferenceAlias} =
                withDefaults(params);

            await API.roleParticipant({
                fetcher,
                body: {
                    role,
                },
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host: clientProps.host,
            });
        },
        funcName: 'setRole',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Enables or disables the spotlight on a participant.
     *
     * Spotlighted participants are locked into primary layout positions
     * ahead of active speakers. The first spotlighted participant occupies
     * the main speaker position. Host-only.
     *
     * REST: `POST /participants/<uuid>/spotlighton` or `spotlightoff`
     */
    const spotlight = generateEndpoint({
        func: async ([params]: GetEndpointParams<'spotlight'>, fetcher) => {
            const {
                enable,
                participantUuid,
                conferenceAlias,
                breakoutUuid,
                host,
            } = withDefaults(params);

            const apiFn = enable
                ? API.spotlightonParticipant
                : API.spotlightoffParticipant;

            return apiFn({
                fetcher,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'spotlight',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Transfers a participant to another conference.
     *
     * If the target conference is PIN-protected, the PIN must be specified.
     * Host-only.
     *
     * REST: `POST /participants/<uuid>/transfer`
     */
    const transfer = generateEndpoint({
        func: async ([params]: GetEndpointParams<'transfer'>, fetcher) => {
            const {
                destination,
                pin,
                role,
                participantUuid,
                conferenceAlias,
                host,
                breakoutUuid,
            } = withDefaults(params);

            const body = {
                conference_alias: destination,
                pin,
                role,
            };

            return API.transferParticipant({
                fetcher,
                body,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'transfer',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    return {
        admit,
        clientMute,
        fecc,
        kick,
        liveCaptions,
        mute,
        muteVideo,
        notifyNotAFK,
        presInMix,
        raiseHand,
        requestAspectRatio,
        sendApplicationMessage,
        sendDTMF,
        sendMessage,
        setRole,
        spotlight,
        transfer,
    };
};
